import {
	Box,
	Button,
	Dialog,
	DialogActions,
	DialogContent,
	DialogTitle,
	IconButton,
	Typography,
} from '@mui/material';
import { closeSnackbar, useSnackbar } from 'notistack';
import { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import CloseIcon from '@mui/icons-material/Close';
import { GridColDef } from '@mui/x-data-grid';
import { ExerciseType } from '@/api/enum/common.enum';
import { DefaultValues, FormProvider, useForm } from 'react-hook-form';
import AppDataGrid, {
	initialPagination,
} from '@/common/components/dataGrid/AppDataGrid';
import AppSummarySearchBar from '@/common/components/form-inputs/AppSummarySearchBar';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import PublishedChip from '@/common/components/PublishedChip';
import { PUBLISHED_LISTB } from '@/common/constants/common';
import CreateOutlinedIcon from '@mui/icons-material/CreateOutlined';
import {
	BlindBoxSeriesDTO,
	SelectTreasureFilter,
} from '@/api/DTO/exchange/box.interface';
import { useGetSelectTreasureListQuery } from '@/api/exchange/boxApiSlice';
import dayjs from 'dayjs';
import { DISPLAY_DATETIME_12H_FORMAT } from '@/utils/dateHelper';

interface Props {
	handleGetSeries: (obj: any) => void;
	gameType: number;
}

export default function AddSeriesDialog(props: Props) {
	const { handleGetSeries, gameType } = props;
	const { t } = useTranslation();
	const { enqueueSnackbar } = useSnackbar();
	const [open, setOpen] = useState(false);
	const [selectedData, setSelectedData] = useState<any[]>([]);

	useEffect(() => {
		setSelectedData([]);
	}, [open]);

	const defaultPropertyFilter: SelectTreasureFilter = {
		pagination: initialPagination,
		searchString: '',
		sortings: [],
	};
	const defaultValues: DefaultValues<SelectTreasureFilter> =
		defaultPropertyFilter;
	const methods = useForm<SelectTreasureFilter>({
		defaultValues,
	});

	const { data: propertySummary, isSuccess: isGetPropertySummarySuccess } =
		useGetSelectTreasureListQuery(
			{ ...methods.getValues(), gameTypeList: [gameType] },
			{
				refetchOnMountOrArgChange: true,
				skip: !open,
			}
		);

	useEffect(() => {}, [methods.watch()]);
	const onSubmit = async () => {
		handleGetSeries(selectedData);
		handleClose();
	};

	const handleClose = () => {
		setOpen(false);
	};
	const handleOpen = () => {
		console.log(gameType);
		if (gameType === undefined || gameType < 0) {
			enqueueSnackbar(t('box.pleaseSelectType'), {
				variant: 'success',
				anchorOrigin: { horizontal: 'center', vertical: 'top' },
				autoHideDuration: 3000,
				action: (key) => {
					return <CloseIcon onClick={() => closeSnackbar(key)} />;
				},
			});
		} else {
			setOpen(true);
		}
	};

	const columns: GridColDef[] = [
		{
			field: 'gameTypeName',
			headerName: t('box.typeName'),
			flex: 21,
		},
		{
			field: 'gameSeriesName',
			headerName: t('box.seriesName'),
			flex: 21,
		},
		{
			field: 'treasureCount',
			headerName: t('box.treasureTotal'),
			flex: 21,
		},
		{
			field: 'published',
			headerName: t('box.seriesStatus'),
			flex: 21,
			renderCell: (params: any) => (
				<PublishedChip
					published={params.row.published}
					publishList={PUBLISHED_LISTB}
				/>
			),
		},
		{
			field: 'publishedEndAt',
			headerName: t('box.seriesPulledTime'),
			flex: 21,
		},
	];

	const handlechange = (text: string) => {
		methods.reset({ ...defaultPropertyFilter, searchString: text });
	};

	const handleSelect = (rows: string[]) => {
		if (rows.length > 1) {
			return enqueueSnackbar(t('exerciseSetting.max1'), {
				variant: 'warning',
				anchorOrigin: { horizontal: 'center', vertical: 'top' },
				autoHideDuration: 3000,
				action: (key) => {
					return <CloseIcon onClick={() => closeSnackbar(key)} />;
				},
			});
		}
		const arr = [
			{
				gameSeriesId: rows[0],
				gameSeriesName: propertySummary?.data?.items.find(
					(item) => item.gameSeriesId === rows[0]
				)?.gameSeriesName,
			},
		];

		setSelectedData(rows.length > 0 ? arr : []);
	};

	const transformResponse = useCallback((response?: BlindBoxSeriesDTO[]) => {
		if (!response) return;
		const newResponse: any[] = [];
		response.forEach((item) => {
			newResponse.push({
				...item,
				publishedEndAt: !item.publishedEndAt
					? '-'
					: dayjs(item.publishedEndAt)
							.locale('en')
							.format(DISPLAY_DATETIME_12H_FORMAT),
			});
		});

		return newResponse;
	}, []);

	const formContent = (
		<>
			<IconButton onClick={() => handleOpen()} color='secondary'>
				<CreateOutlinedIcon />
			</IconButton>
			<Dialog
				open={open}
				onClose={() => handleClose()}
				aria-labelledby='alert-dialog-title'
				aria-describedby='alert-dialog-description'
				maxWidth='lg'
				fullWidth
			>
				<DialogTitle variant='h5'>
					{t('taskManage.designatedSeries')}
				</DialogTitle>
				<IconButton
					aria-label='close'
					onClick={() => handleClose()}
					sx={{
						position: 'absolute',
						right: 8,
						top: 8,
						color: (theme) => theme.palette.grey[500],
					}}
				>
					<CloseIcon />
				</IconButton>
				<DialogContent>
					<Box
						display='flex'
						justifyContent='space-between'
						alignItems='center'
						marginBottom={'10px'}
					>
						<Typography
							variant='body2'
							sx={{
								display: 'flex',
							}}
						>
							<ErrorOutlineIcon sx={{ fontSize: '20px' }} />
							&nbsp;
							{t('box.tips')}
						</Typography>
					</Box>
					<FormProvider {...methods}>
						<AppSummarySearchBar
							name='searchString'
							control={methods.control}
							placeholder={t('common.search')}
							onchange={(text) => {
								handlechange(text);
							}}
						/>
						<div style={{ height: '20px' }}></div>
						<AppDataGrid
							checkboxSelection
							loading={!isGetPropertySummarySuccess}
							rows={transformResponse(propertySummary?.data?.items) ?? []}
							rowCount={propertySummary?.data?.pagination.totalItems ?? 0}
							columns={columns}
							getRowId={(row) => row.gameSeriesId}
							onSelectionModelChange={(selectionModel) => {
								handleSelect(selectionModel as string[]);
							}}
							selectionModel={selectedData.map(
								(item: { gameSeriesId: any }) => item.gameSeriesId
							)}
						/>
					</FormProvider>
				</DialogContent>
				<DialogActions>
					<Button onClick={() => handleClose()}>{t('button.cancel')}</Button>

					<Button type='submit' variant='contained' onClick={() => onSubmit()}>
						{t('box.change')}
					</Button>
				</DialogActions>
			</Dialog>
		</>
	);
	return <>{formContent}</>;
}
